import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCcw, AlertCircle, CheckCircle, XCircle, Mail, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import SharedHeader from "@/components/SharedHeader";
import SharedFooter from "@/components/SharedFooter";

export default function RefundPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <SharedHeader 
          transparent={true}
          rightContent={
            <Link to="/">
              <Button variant="ghost">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          }
        />
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-10">
          
          {/* Last Updated */}
          <p className="text-sm text-gray-500 mb-6">
            Last Updated: May 12, 2025
          </p>

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Refund & Cancellation Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              At Eduzy, we want you to be completely satisfied with our educational services. This policy 
              outlines our refund and cancellation terms for subscriptions purchased through our platform.
            </p>
          </section>

          {/* Quick Summary */}
          <section className="mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-emerald-800 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Quick Summary
              </h3>
              <ul className="space-y-2 text-emerald-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-1 flex-shrink-0" />
                  <span><strong>90-Day Free Trial:</strong> No payment required during trial - cancel anytime</span>
                </li>
                <li className="flex items-start gap-2">
                  <XCircle className="w-4 h-4 mt-1 flex-shrink-0 text-amber-600" />
                  <span><strong>After Subscription:</strong> No refunds, but you can cancel future renewals anytime</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Free Trial */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Free Trial Period</h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                All new users receive a <strong>90-day free trial</strong> with the following terms:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>No payment information is required to start the trial</li>
                <li>Access to all subjects with a daily limit of 5 interactions</li>
                <li>Trial can be cancelled at any time with no charges</li>
                <li>At the end of the trial, you may choose to subscribe or let access expire</li>
                <li>No automatic conversion to paid subscription without explicit consent</li>
              </ul>
            </div>
          </section>

          {/* No Refunds Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Refund Policy</h2>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-amber-800 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                <strong>No refunds after subscription purchase</strong>
              </p>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              Since we offer a generous 90-day free trial with no payment required, we do not offer refunds 
              on paid subscriptions. The free trial gives you ample time to evaluate our service before 
              committing to a paid plan.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              However, you can:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Cancel auto-renewal:</strong> Stop future charges while retaining access until the current period ends</li>
              <li><strong>Switch plans:</strong> Downgrade or change subjects (pro-rated adjustments may apply)</li>
            </ul>
          </section>

          {/* Cancellation Process */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How to Cancel</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You can cancel your subscription at any time through the following methods:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 border rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Parent Dashboard</h4>
                <ol className="text-gray-700 text-sm space-y-1 list-decimal list-inside">
                  <li>Log in to Parent Dashboard</li>
                  <li>Go to "Subscription" tab</li>
                  <li>Click "Manage Subscription"</li>
                  <li>Select "Cancel Subscription"</li>
                  <li>Confirm cancellation</li>
                </ol>
              </div>
              <div className="bg-gray-50 border rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Email Request</h4>
                <p className="text-gray-700 text-sm mb-2">
                  Send an email to:
                </p>
                <a 
                  href="mailto:support@contentiumtech.com" 
                  className="text-emerald-600 hover:underline flex items-center gap-1"
                >
                  <Mail className="w-4 h-4" />
                  support@contentiumtech.com
                </a>
                <p className="text-gray-500 text-xs mt-2">
                  Include your registered email and child's name
                </p>
              </div>
            </div>
          </section>

          {/* What Happens After Cancellation */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. After Cancellation</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you cancel your subscription:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Continued Access:</strong> You retain full access until the end of your current billing period</li>
              <li><strong>No Future Charges:</strong> Your payment method will not be charged again</li>
              <li><strong>Data Retention:</strong> Your child's learning data is retained for 90 days after subscription ends</li>
              <li><strong>Reactivation:</strong> You can resubscribe at any time to restore access</li>
              <li><strong>Progress Preserved:</strong> Learning progress, levels, and achievements are preserved if you resubscribe within 90 days</li>
            </ul>
          </section>

          {/* B2B School Subscriptions */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. School (B2B) Subscriptions</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For schools and institutional subscriptions:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Refund terms are as per the signed agreement/contract</li>
              <li>Early termination fees may apply as specified in the contract</li>
              <li>Pro-rated refunds may be available for annual subscriptions (subject to contract terms)</li>
              <li>Email <a href="mailto:schools@contentiumtech.com" className="text-emerald-600 hover:underline">schools@contentiumtech.com</a> for assistance</li>
            </ul>
          </section>

          {/* Exceptional Circumstances */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Exceptional Circumstances</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may consider refunds outside the standard policy in the following cases:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Technical Issues:</strong> Persistent platform issues preventing usage (verified by our support team)</li>
              <li><strong>Billing Errors:</strong> Duplicate charges or incorrect amounts</li>
              <li><strong>Unauthorized Purchases:</strong> Purchases made without parental consent (requires verification)</li>
              <li><strong>Service Discontinuation:</strong> If we discontinue the service in your region</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              All exceptional refund requests are reviewed on a case-by-case basis. Please contact 
              support with relevant details and documentation.
            </p>
          </section>

          {/* Refund Processing */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Refund Processing</h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                When a refund is approved:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Processing Time:</strong> 5-7 business days to process</li>
                <li><strong>Bank Processing:</strong> Additional 3-5 business days for the amount to reflect in your account</li>
                <li><strong>Original Payment Method:</strong> Refunds are credited to the original payment method used</li>
                <li><strong>Confirmation:</strong> You will receive an email confirmation when the refund is processed</li>
              </ul>
            </div>
          </section>

          {/* Contact */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For refund requests or questions about this policy:
            </p>
            <div className="bg-gray-50 border rounded-lg p-4 space-y-2">
              <p className="flex items-center gap-2 text-gray-700">
                <Mail className="w-4 h-4 text-emerald-600" />
                <strong>Email:</strong> <a href="mailto:support@contentiumtech.com" className="text-emerald-600 hover:underline">support@contentiumtech.com</a>
              </p>
              <p className="text-gray-700">
                <strong>Subject Line:</strong> Refund Request - [Your Registered Email]
              </p>
              <p className="text-gray-500 text-sm mt-2">
                Please include your registered email, child's name, subscription details, and reason for the refund request.
              </p>
            </div>
          </section>

          {/* Policy Changes */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Refund & Cancellation Policy from time to time. Any changes will be 
              posted on this page with an updated "Last Updated" date. Continued use of our services 
              after changes constitutes acceptance of the revised policy.
            </p>
          </section>

          {/* Footer Links */}
          <div className="border-t pt-6 mt-8">
            <p className="text-sm text-gray-500 mb-4">Related Policies:</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/terms" className="text-emerald-600 hover:underline text-sm">Terms of Service</Link>
              <Link to="/privacy" className="text-emerald-600 hover:underline text-sm">Privacy Policy</Link>
              <Link to="/faq" className="text-emerald-600 hover:underline text-sm">FAQ</Link>
            </div>
          </div>

        </div>
      </main>

      {/* Footer */}
      <SharedFooter />
    </div>
  );
}
